/**
 * Canal temps réel du Copilot.
 *
 * Le backend pousse transcription et suggestions ; le hook n'interroge jamais
 * l'API en boucle (le plan l'interdit explicitement). Reconnexion avec recul
 * progressif : une coupure réseau pendant un appel ne doit pas faire perdre la
 * suite de la conversation.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { API, getToken } from "@/lib/crmApi";

const WS_BASE = API.replace(/^http/, "ws");
const MAX_TRANSCRIPT = 200;
const RETRY_MS = [1000, 2000, 4000, 8000, 15000];

export default function useCopilotSocket(sessionId, { enabled = true } = {}) {
  const [connected, setConnected] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [partial, setPartial] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  // Faux, ou l'événement `copilot.thinking` lui-même : `thinking.live` dit que
  // la suggestion est rédigée en direct — le panneau prévient alors le
  // commercial de ne pas l'attendre (une réponse préparée arrive sans ça).
  const [thinking, setThinking] = useState(false);
  const [warning, setWarning] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  // Banque de réponses préparées côté serveur : { tempo, moments, answers }.
  // `tempo` est la phrase à dire pendant un blanc ; `answers` le catalogue des
  // répliques prêtes, affiché dans la colonne « questions & informations ».
  const [prepared, setPrepared] = useState(null);
  // Mode du copilote pour CET appel, tel que le serveur l'annonce. Vrai par
  // défaut : c'est l'état du moteur tant qu'aucun commercial ne l'a coupé, et
  // la première `session.updated` corrige au besoin dans la foulée.
  const [autoSuggest, setAutoSuggest] = useState(true);
  // Verrou posé par l'administrateur sur la fiche du commercial : le mode
  // automatique est retiré, l'interrupteur ne peut plus le rallumer.
  const [autoSuggestLocked, setAutoSuggestLocked] = useState(false);
  const socketRef = useRef(null);
  const attemptRef = useRef(0);
  const closedRef = useRef(false);
  // Appel analysé : plus rien ne viendra. Sans ce drapeau, le panneau laissé
  // ouvert pour lire le compte rendu continuait de rouvrir un WebSocket, et
  // chaque tentative recréait une session vide côté serveur.
  const finishedRef = useRef(false);

  const send = useCallback((action, data = {}) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action, data }));
  }, []);

  useEffect(() => {
    if (!enabled || !sessionId) return undefined;
    closedRef.current = false;
    finishedRef.current = false;
    // Nouvel appel : on repart d'un fil vide, sinon la conversation précédente
    // s'affichait au-dessus de la nouvelle.
    setTranscript([]); setPartial(null); setSuggestion(null);
    setThinking(false); setWarning(null); setAnalysis(null); setPrepared(null);
    let timer = null;

    const open = () => {
      if (closedRef.current || finishedRef.current) return;
      const ws = new WebSocket(`${WS_BASE}/copilot/ws/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(getToken() || "")}`);
      socketRef.current = ws;

      ws.onopen = () => { attemptRef.current = 0; setConnected(true); };

      ws.onmessage = (event) => {
        let message;
        try { message = JSON.parse(event.data); } catch { return; }
        const { type, data } = message;

        if (type === "transcript.partial") {
          setPartial(data);
        } else if (type === "transcript.final") {
          setPartial(null);
          setTranscript((prev) => [...prev, data].slice(-MAX_TRANSCRIPT));
        } else if (type === "copilot.thinking") {
          setThinking(data || true);
        } else if (type === "copilot.suggestion") {
          setThinking(false);
          setSuggestion(data);
        } else if (type === "copilot.suggestion.dropped") {
          // Règle 1 du plan « Zéro temps mort » : une suggestion arrivée trop
          // tard est abandonnée côté serveur — on efface juste le « rédige »,
          // sans rien afficher à sa place.
          setThinking(false);
        } else if (type === "copilot.prepared") {
          setPrepared(data);
        } else if (type === "copilot.warning") {
          setThinking(false);
          setWarning(data);
        } else if (type === "call.analysed") {
          finishedRef.current = true;
          setAnalysis(data);
        } else if (type === "session.updated") {
          // Le serveur fait foi sur le mode du copilote : il l'annonce à
          // l'ouverture et le rediffuse à chaque bascule, y compris celle d'un
          // autre onglet ouvert sur le même appel.
          if (typeof data?.auto_suggest === "boolean") setAutoSuggest(data.auto_suggest);
          if (typeof data?.auto_suggest_locked === "boolean") setAutoSuggestLocked(data.auto_suggest_locked);
          // Reconnexion : la banque préparée existait déjà côté serveur, elle
          // revient (phrase de temporisation + catalogue) avec l'état de session.
          if (data?.tempo) {
            setPrepared((prev) => prev
              || { tempo: data.tempo, answers: data.prepared_answers || [] });
          }
          if (Array.isArray(data?.transcript)) {
            // Reprise après reconnexion : le serveur renvoie l'historique. On ne
            // remplace que s'il en sait plus que nous — une session déjà purgée
            // côté serveur renverrait un fil vide, qui effacerait l'appel affiché.
            setTranscript((prev) => (data.transcript.length > prev.length
              ? data.transcript.slice(-MAX_TRANSCRIPT) : prev));
          }
        }
      };

      ws.onclose = () => {
        setConnected(false);
        socketRef.current = null;
        if (closedRef.current || finishedRef.current) return;
        const delay = RETRY_MS[Math.min(attemptRef.current, RETRY_MS.length - 1)];
        attemptRef.current += 1;
        timer = setTimeout(open, delay);
      };

      ws.onerror = () => { try { ws.close(); } catch { /* déjà fermé */ } };
    };

    open();
    return () => {
      closedRef.current = true;
      if (timer) clearTimeout(timer);
      try { socketRef.current?.close(); } catch { /* déjà fermé */ }
      socketRef.current = null;
    };
  }, [sessionId, enabled]);

  const reset = useCallback(() => {
    setTranscript([]); setPartial(null); setSuggestion(null);
    setThinking(false); setWarning(null); setAnalysis(null);
  }, []);

  // Fin d'appel sans conversation : plus rien ne viendra. Le panneau coupe le
  // canal lui-même plutôt que de le laisser se reconnecter en boucle — et
  // recréer à chaque tentative une session vide côté serveur.
  const disconnect = useCallback(() => {
    finishedRef.current = true;
    try { socketRef.current?.close(); } catch { /* déjà fermé */ }
    socketRef.current = null;
  }, []);

  // Bascule optimiste : l'interrupteur doit répondre au doigt, la confirmation
  // arrive par `session.updated`. Une socket fermée ne change rien — le serveur
  // reste la référence, et la reconnexion réannonce le mode réel.
  const setAuto = useCallback((enabled) => {
    if (autoSuggestLocked) return;           // l'admin a tranché : rien à envoyer
    setAutoSuggest(Boolean(enabled));
    send("auto_suggest", { enabled: Boolean(enabled) });
  }, [send, autoSuggestLocked]);

  return { connected, transcript, partial, suggestion, thinking, warning, analysis,
           prepared, autoSuggest, autoSuggestLocked, setAuto, send, reset, disconnect,
           dismissSuggestion: () => setSuggestion(null) };
}
